// "Drag and drop works anywhere" (ECN-017; shell-ia §12.3): files dragged over the frame show a drop zone. Over the agent column it reads
// "Drop to attach to the conversation"; elsewhere "Drop to add to the Knowledge Bank", which opens the Knowledge Bank upload sheet with the
// files (`openKbUpload` of econic/knowledge, WP-KB-UI). A screen may claim drops on its own area with `useDropTarget`. Desk frames handle
// their own drops (iframes do not bubble drag events). WP-SHELL.
import { useEffect, useRef, useState } from "react";
import { FileUp, Paperclip } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import { useWorkspace } from "../../app/workspace-context";
import { claimFor, type DropClaim } from "./useDropTarget";

const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes("Files");

type Target = { kind: "kb" } | { kind: "chat" } | { kind: "claim"; claim: DropClaim };

export function GlobalDrop() {
  const { t } = useTranslation();
  const { can } = useWorkspace();
  const [target, setTarget] = useState<Target | null>(null);
  const depth = useRef(0);
  const canKb = can("knowledge.upload") || can("knowledge.read");

  useEffect(() => {
    const where = (e: DragEvent): Target | null => {
      const el = e.target instanceof Element ? e.target : null;
      const claim = el ? claimFor(el) : null;
      if (claim) return { kind: "claim", claim };
      if (el?.closest("aside[data-dock-panel]")) return { kind: "chat" };
      return canKb ? { kind: "kb" } : null;
    };
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current += 1;
      setTarget(where(e));
    };
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      const next = where(e);
      if (next) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      }
      setTarget((cur) => (cur?.kind === next?.kind && (cur?.kind !== "claim" || (next?.kind === "claim" && cur.claim === next.claim)) ? cur : next));
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setTarget(null);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      const at = where(e);
      depth.current = 0;
      setTarget(null);
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!at || files.length === 0) return;
      e.preventDefault();
      if (at.kind === "claim") {
        at.claim.onDrop(files);
        return;
      }
      if (at.kind === "chat") {
        // The conversation's own composer takes attachments (WP-SIDEBAR); until it does, say so instead of dropping them silently.
        const composer = document.querySelector<HTMLElement>("aside[data-dock-panel] [data-attach-files]");
        if (composer) composer.dispatchEvent(new CustomEvent("econic:attach", { detail: { files } }));
        else toast.info(t("assistant:econic.shell.drop.chatUnavailable"));
        return;
      }
      void import("../knowledge").then((m) => {
        const open = (m as unknown as Record<string, unknown>).openKbUpload as ((files: File[]) => void) | undefined;
        if (open) open(files);
        else toast.info(t("assistant:econic.shell.drop.kbUnavailable"));
      });
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
  }, [canKb, t]);

  if (!target) return null;
  const label = target.kind === "claim" ? target.claim.label : target.kind === "chat" ? t("assistant:econic.shell.drop.chat") : t("assistant:econic.shell.drop.kb");
  const hint = target.kind === "kb" ? t("assistant:econic.shell.drop.kbHint") : null;
  return (
    <div className="es-drop" data-target={target.kind === "chat" ? "chat" : "content"} aria-hidden>
      <div className="zone">
        {target.kind === "chat" ? <Paperclip /> : <FileUp />}
        <span>{label}</span>
        {hint ? <small>{hint}</small> : null}
      </div>
    </div>
  );
}
